import { FC, useMemo } from "react"
import {
  CONFIG_ADMIN_INPUT_FULL_CLASS,
  ConfigAdminNumberInput,
  ConfigAdminPanelStackedField,
  ConfigAdminToggleRow
} from "../../../config-admin-form-primitives"
import { ConfigAdminPanel } from "../../../config-admin-panel"
import Skeleton from "../../../../skeletons/base"
import {
  RateCardsConfigDetailsData,
  ScenarioPreferenceMapResponse
} from "../../../../../../types"
import LoanTypePanel from "./LoanTypePanel"
import { isRefi, toLoanTypes } from "./utils"

interface Props {
  details: RateCardsConfigDetailsData
  setDetails: (details: RateCardsConfigDetailsData) => void
  tab: string
  scenarioPreferences?: ScenarioPreferenceMapResponse
  isLoadingScenarios?: boolean
}

const ManageRateCardsFormContent: FC<Props> = ({
  details,
  setDetails,
  tab,
  scenarioPreferences,
  isLoadingScenarios
}) => {
  const { purchaseLoanTypes, refinanceLoanTypes } = useMemo(() => {
    const prefs = scenarioPreferences?.profile_preferences
    if (!prefs) return { purchaseLoanTypes: [], refinanceLoanTypes: [] }
    const activeKeys = Object.entries(prefs)
      .filter(([, v]) => v != null)
      .map(([k]) => k)
    const purchase = toLoanTypes(activeKeys.filter(k => !isRefi(k)))
    const refinance = toLoanTypes(activeKeys.filter(isRefi))
    return { purchaseLoanTypes: purchase, refinanceLoanTypes: refinance }
  }, [scenarioPreferences])

  const updateField = <K extends keyof RateCardsConfigDetailsData>(
    field: K,
    value: RateCardsConfigDetailsData[K]
  ) => {
    setDetails({ ...details, [field]: value })
  }

  if (tab === "general") {
    return (
      <div className="space-y-6 pt-2">
        <ConfigAdminPanel>
          <ConfigAdminPanelStackedField
            label="Max Results"
            fieldKey="max_results"
            hint="Sets the maximum number of mortgage rate results displayed to the user"
            htmlFor="max-results"
          >
            <ConfigAdminNumberInput
              id="max-results"
              value={details.max_results}
              onChange={v => updateField("max_results", v ?? 0)}
              parse="int"
              min={1}
              className={CONFIG_ADMIN_INPUT_FULL_CLASS}
            />
          </ConfigAdminPanelStackedField>

          <ConfigAdminToggleRow
            label="Disable CTA"
            fieldKey="disable_cta"
            description='Hides or disables the "Customize Rate" call-to-action button'
            enabled={details.disable_cta ?? false}
            onChange={(value: boolean) => updateField("disable_cta", value)}
            last={false}
          />

          <ConfigAdminToggleRow
            label="Disable Average"
            fieldKey="disable_avg"
            description='Hides the "National Average" above the product tabs'
            enabled={details.disable_avg ?? false}
            onChange={(value: boolean) => updateField("disable_avg", value)}
            last={false}
          />
        </ConfigAdminPanel>
      </div>
    )
  }

  if (tab === "terms") {
    if (isLoadingScenarios || !scenarioPreferences) {
      return (
        <div className="space-y-6 pt-2">
          <ConfigAdminPanel>
            <Skeleton>
              <div className="divide-y divide-gray-100 px-4">
                {Array.from({ length: 5 }, (_, i) => (
                  <div key={i} className="flex items-center gap-4 py-3">
                    <div className="h-9 flex-1 rounded-md bg-gray-200" />
                    <div className="h-5 w-10 rounded-full bg-gray-200" />
                  </div>
                ))}
              </div>
            </Skeleton>
          </ConfigAdminPanel>
        </div>
      )
    }

    const hasScenarios =
      purchaseLoanTypes.length > 0 || refinanceLoanTypes.length > 0

    if (!hasScenarios) {
      return (
        <div className="space-y-6 pt-2">
          <ConfigAdminPanel>
            <p className="px-4 py-8 text-center text-sm text-gray-500">
              No scenario preferences configured for this brand. Open Settings
              to configure scenario preferences first.
            </p>
          </ConfigAdminPanel>
        </div>
      )
    }

    return (
      <div className="space-y-6 pt-2">
        <LoanTypePanel
          group="purchase"
          label="Purchase"
          loanTypes={purchaseLoanTypes}
          details={details}
          setDetails={setDetails}
        />
        <LoanTypePanel
          group="refinance"
          label="Refinance"
          loanTypes={refinanceLoanTypes}
          details={details}
          setDetails={setDetails}
        />
      </div>
    )
  }

  return null
}

export default ManageRateCardsFormContent
